import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button, Input, InputNumber, Select, Switch, Tag, Modal, Empty, Radio } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { api_product } from '../../api/product';
import { printDocument } from '../../utils/print-utils';
import '../../assets/css/barcode-print.css';

export interface PriceTagPrintItem {
    id: number;
    product_code: string;
    product_name: string;
    price_retail: number | string;
    price_wholesale?: number | string;
    product_unit_name?: string;
    copies: number;
}

export type TagTemplateStyle = 'shelf' | 'promo' | 'standard' | 'minimal';
export type BorderStyleType = 'dashed' | 'solid' | 'double' | 'none';
export type PrintRotation = 0 | 90 | 180 | 270;

/**
 * Calculates adaptive font sizes for the price row (Prefix, Number, Unit)
 * tailored to avoid clipping or wrapping while maximizing visibility on 38x28mm stickers.
 */
export const getAdaptivePriceFontSize = (price: number | string, isSmall: boolean, isPreview: boolean = false) => {
    const formatted = Number(price || 0).toLocaleString();
    const len = formatted.length;

    if (!isSmall) {
        return {
            priceSize: isPreview ? '28px' : '28pt',
            prefixSize: isPreview ? '14px' : '14pt',
            unitSize: isPreview ? '11px' : '10pt'
        };
    }

    // Small sticker preset (38x28mm, 40x30mm) - Approach 1: Prominent and well-filled
    if (len <= 2) {
        // e.g. "5", "20", "99"
        return {
            priceSize: isPreview ? '28px' : '25pt',
            prefixSize: isPreview ? '12px' : '11pt',
            unitSize: isPreview ? '10px' : '8.5pt'
        };
    } else if (len === 3) {
        // e.g. "120", "999"
        return {
            priceSize: isPreview ? '24px' : '22pt',
            prefixSize: isPreview ? '11px' : '10pt',
            unitSize: isPreview ? '9px' : '8pt'
        };
    } else if (len <= 5) {
        // e.g. "1,250", "9,999"
        return {
            priceSize: isPreview ? '21px' : '18.5pt',
            prefixSize: isPreview ? '10px' : '9pt',
            unitSize: isPreview ? '8.5px' : '7.5pt'
        };
    } else if (len <= 7) {
        // e.g. "12,500", "99,999"
        return {
            priceSize: isPreview ? '17px' : '15pt',
            prefixSize: isPreview ? '9px' : '8pt',
            unitSize: isPreview ? '8px' : '7pt'
        };
    } else {
        // e.g. "125,000+"
        return {
            priceSize: isPreview ? '14px' : '12.5pt',
            prefixSize: isPreview ? '8.5px' : '7.5pt',
            unitSize: isPreview ? '7.5px' : '6.5pt'
        };
    }
};

/**
 * Calculates adaptive category for product name based on length
 */
export const getAdaptiveProductNameCategory = (name: string) => {
    const len = (name || '').trim().length;
    if (len <= 10) return 'very-short';
    if (len <= 16) return 'short';
    if (len <= 25) return 'medium';
    if (len <= 35) return 'long';
    return 'very-long';
};

export const getAdaptiveProductNameFontSize = (name: string, isSmall: boolean, isPreview: boolean = false) => {
    const category = getAdaptiveProductNameCategory(name);
    if (!isSmall) {
        if (category === 'very-short' || category === 'short') return isPreview ? '16px' : '14pt';
        if (category === 'medium') return isPreview ? '14px' : '12pt';
        return isPreview ? '12px' : '10.5pt';
    }

    // isSmall (38x28mm rotated)
    switch (category) {
        case 'very-short':
            return isPreview ? '13px' : '11.5pt';
        case 'short':
            return isPreview ? '11px' : '9.5pt';
        case 'medium':
            return isPreview ? '9.5px' : '8pt'; // e.g. "กาวิสคอน(กล่อง4ซอง)" fits completely without ellipsis
        case 'long':
            return isPreview ? '8.5px' : '7.2pt';
        case 'very-long':
        default:
            return isPreview ? '7.5px' : '6.5pt';
    }
};

export const PriceTagPrint: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const priceTagGridRef = useRef<HTMLDivElement>(null);
    const [products, setProducts] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [selectedItems, setSelectedItems] = useState<PriceTagPrintItem[]>([]);
    const [searchQuery, setSearchQuery] = useState('');

    const [storeName, setStoreName] = useState('JINDA POS');

    // Designer & Layout configurations (Customer Price & Promo Tag)
    const [templateStyle, setTemplateStyle] = useState<TagTemplateStyle>('shelf');
    const [showStoreName, setShowStoreName] = useState(true);
    const [showProductName, setShowProductName] = useState(true);
    const [showProductCode, setShowProductCode] = useState(true);
    const [showPrice, setShowPrice] = useState(true);
    const [showUnit, setShowUnit] = useState(true);
    const [showDate, setShowDate] = useState(false);
    const [promoText, setPromoText] = useState('🔥 ราคาพิเศษ / SPECIAL OFFER');
    const [pricePrefix, setPricePrefix] = useState('฿');
    const [accentColor, setAccentColor] = useState('#FF7A59');
    const [borderStyle, setBorderStyle] = useState<BorderStyleType>('solid');
    const [tagSize, setTagSize] = useState<'38x28' | '40x30' | '30x40' | 'shelf' | 'standard' | 'large' | 'compact' | 'thermal58' | 'thermal80' | 'a4' | 'custom'>('38x28');
    const [a4GridSize, setA4GridSize] = useState<'38x28' | '50x30' | '65x35' | '80x50'>('38x28');
    const [printColorMode, setPrintColorMode] = useState<'color' | 'mono'>('mono');
    const [rotation, setRotation] = useState<PrintRotation>(() => {
        const saved = localStorage.getItem('jinda_print_rotation');
        return saved && [0, 90, 180, 270].includes(Number(saved)) ? (Number(saved) as PrintRotation) : 0;
    });

    const handleRotationChange = (val: PrintRotation) => {
        setRotation(val);
        try { localStorage.setItem('jinda_print_rotation', String(val)); } catch (e) {}
    };

    const [customWidth, setCustomWidth] = useState<number>(() => {
        const saved = localStorage.getItem('jinda_price_tag_custom_w');
        return saved && Number(saved) > 0 ? Number(saved) : 38;
    });
    const [customHeight, setCustomHeight] = useState<number>(() => {
        const saved = localStorage.getItem('jinda_price_tag_custom_h');
        return saved && Number(saved) > 0 ? Number(saved) : 28;
    });

    const handleCustomWidthChange = (val: number | null) => {
        const num = val && val > 0 ? val : 38;
        setCustomWidth(num);
        try { localStorage.setItem('jinda_price_tag_custom_w', String(num)); } catch (e) {}
    };

    const handleCustomHeightChange = (val: number | null) => {
        const num = val && val > 0 ? val : 28;
        setCustomHeight(num);
        try { localStorage.setItem('jinda_price_tag_custom_h', String(num)); } catch (e) {}
    };

    const getEffectiveDimensions = () => {
        switch (tagSize) {
            case '38x28':
                return { width: 38, height: 28, isSmall: true, isContinuous: false, isA4: false, a4Cols: 4, a4CardW: 38, a4CardH: 28 };
            case '40x30':
                return { width: 40, height: 30, isSmall: true, isContinuous: false, isA4: false, a4Cols: 4, a4CardW: 40, a4CardH: 30 };
            case '30x40':
                return { width: 30, height: 40, isSmall: true, isContinuous: false, isA4: false, a4Cols: 4, a4CardW: 30, a4CardH: 40 };
            case 'compact':
                return { width: 35, height: 25, isSmall: true, isContinuous: false, isA4: false, a4Cols: 4, a4CardW: 35, a4CardH: 25 };
            case 'standard':
                return { width: 60, height: 40, isSmall: false, isContinuous: false, isA4: false, a4Cols: 3, a4CardW: 60, a4CardH: 40 };
            case 'shelf':
                return { width: 80, height: 50, isSmall: false, isContinuous: false, isA4: false, a4Cols: 2, a4CardW: 80, a4CardH: 50 };
            case 'large':
                return { width: 100, height: 70, isSmall: false, isContinuous: false, isA4: false, a4Cols: 2, a4CardW: 100, a4CardH: 70 };
            case 'thermal58':
                return { width: 58, height: 0, isSmall: false, isContinuous: true, isA4: false, a4Cols: 1, a4CardW: 58, a4CardH: 0 };
            case 'thermal80':
                return { width: 80, height: 0, isSmall: false, isContinuous: true, isA4: false, a4Cols: 1, a4CardW: 80, a4CardH: 0 };
            case 'a4': {
                const colCount = a4GridSize === '80x50' ? 2 : a4GridSize === '65x35' || a4GridSize === '50x30' ? 3 : 4;
                const cardH = a4GridSize === '80x50' ? 50 : a4GridSize === '65x35' ? 35 : a4GridSize === '50x30' ? 30 : 28;
                const cardW = a4GridSize === '80x50' ? 80 : a4GridSize === '65x35' ? 65 : a4GridSize === '50x30' ? 50 : 38;
                return { width: 210, height: 297, isSmall: cardW <= 45, isContinuous: false, isA4: true, a4Cols: colCount, a4CardW: cardW, a4CardH: cardH };
            }
            case 'custom': {
                const w = Math.max(10, Number(customWidth) || 38);
                const h = Math.max(10, Number(customHeight) || 28);
                return { width: w, height: h, isSmall: w <= 45, isContinuous: false, isA4: false, a4Cols: 3, a4CardW: w, a4CardH: h };
            }
            default:
                return { width: 38, height: 28, isSmall: true, isContinuous: false, isA4: false, a4Cols: 4, a4CardW: 38, a4CardH: 28 };
        }
    };

    useEffect(() => {
        if (!searchQuery.trim()) {
            loadProducts();
            return;
        }
        const timer = setTimeout(async () => {
            setLoading(true);
            try {
                const res = await api_product('LIST', { search: searchQuery.trim(), page: 1, page_size: 100 });
                const list = Array.isArray(res) ? res : (res?.items || []);
                if (Array.isArray(list)) {
                    setProducts(list);
                }
            } catch (e) {
                console.error('Failed to search products', e);
            } finally {
                setLoading(false);
            }
        }, 300);
        return () => clearTimeout(timer);
    }, [searchQuery]);

    const loadProducts = async () => {
        setLoading(true);
        try {
            const res = await api_product('LIST', { page: 1, page_size: 100 });
            const list = Array.isArray(res) ? res : (res?.items || []);
            if (Array.isArray(list)) {
                setProducts(list);

                // Auto add preselected product if passed via state/query
                const queryParams = new URLSearchParams(location.search);
                const queryPid = queryParams.get('productId');
                const targetId = (location.state as any)?.productId || (queryPid ? Number(queryPid) : null);
                if (targetId) {
                    const targetProd = list.find((p: any) => p.id === targetId);
                    if (targetProd && targetProd.product_code) {
                        setSelectedItems([{
                            id: targetProd.id,
                            product_code: targetProd.product_code,
                            product_name: targetProd.product_name,
                            price_retail: targetProd.price_retail || 0,
                            price_wholesale: targetProd.price_wholesale,
                            product_unit_name: targetProd.product_unit_name,
                            copies: 1
                        }]);
                    }
                }
            }
        } catch (e) {
            console.error('Failed to load products', e);
        }
        setLoading(false);
    };

    const filteredProductOptions = useMemo(() => {
        return products;
    }, [products]);

    const handleAddProduct = (productId: number) => {
        const prod = products.find(p => p.id === productId);
        if (!prod || !prod.product_code) return;

        const existingIndex = selectedItems.findIndex(item => item.id === prod.id);
        if (existingIndex >= 0) {
            const updated = [...selectedItems];
            updated[existingIndex].copies += 1;
            setSelectedItems(updated);
        } else {
            setSelectedItems([...selectedItems, {
                id: prod.id,
                product_code: prod.product_code,
                product_name: prod.product_name,
                price_retail: prod.price_retail || 0,
                price_wholesale: prod.price_wholesale,
                product_unit_name: prod.product_unit_name,
                copies: 1
            }]);
        }
    };

    const handleAddAllFiltered = () => {
        const newItems: PriceTagPrintItem[] = [...selectedItems];
        filteredProductOptions.forEach(prod => {
            if (!prod.product_code) return;
            const idx = newItems.findIndex(i => i.id === prod.id);
            if (idx >= 0) {
                newItems[idx].copies += 1;
            } else {
                newItems.push({
                    id: prod.id,
                    product_code: prod.product_code,
                    product_name: prod.product_name,
                    price_retail: prod.price_retail || 0,
                    price_wholesale: prod.price_wholesale,
                    product_unit_name: prod.product_unit_name,
                    copies: 1
                });
            }
        });
        setSelectedItems(newItems);
    };

    const handleUpdateCopies = (id: number, copies: number) => {
        const updated = selectedItems.map(item =>
            item.id === id ? { ...item, copies: Math.max(1, copies) } : item
        );
        setSelectedItems(updated);
    };

    const handleRemoveItem = (id: number) => {
        setSelectedItems(selectedItems.filter(item => item.id !== id));
    };

    const handleClearAll = () => {
        setSelectedItems([]);
    };

    const totalLabelsCount = useMemo(() => {
        return selectedItems.reduce((sum, item) => sum + (item.copies || 0), 0);
    }, [selectedItems]);

    const previewLabels = useMemo(() => {
        const labels: PriceTagPrintItem[] = [];
        selectedItems.forEach(item => {
            for (let i = 0; i < (item.copies || 1); i++) {
                labels.push(item);
            }
        });
        return labels;
    }, [selectedItems]);

    const handlePrint = () => {
        if (previewLabels.length === 0) {
            Modal.warning({
                title: 'ยังไม่มีรายการสำหรับพิมพ์',
                content: 'กรุณาเลือกสินค้าอย่างน้อย 1 รายการก่อนสั่งพิมพ์',
                okText: 'เข้าใจแล้ว'
            });
            return;
        }
        if (priceTagGridRef.current) {
            const dims = getEffectiveDimensions();
            let docType: any = 'barcode-label';
            let customPrintCss = '';

            if (dims.isContinuous) {
                docType = dims.width === 58 ? 'thermal-58mm' : 'thermal-80mm';
            } else if (dims.isA4) {
                docType = 'a4-portrait';
                const isColor = printColorMode === 'color';
                const cols = (dims as any).a4Cols || 4;
                const cardH = (dims as any).a4CardH || 28;
                customPrintCss = `
                    @page {
                        size: A4 portrait;
                        margin: 8mm 6mm;
                    }
                    * {
                        box-sizing: border-box !important;
                        -webkit-print-color-adjust: exact !important;
                        print-color-adjust: exact !important;
                    }
                    html, body {
                        margin: 0 !important;
                        padding: 0 !important;
                        background: #FFFFFF !important;
                        color: #000000 !important;
                    }
                    .no-print, .no-print * {
                        display: none !important;
                    }
                    .barcode-preview-container {
                        background: transparent !important;
                        padding: 0 !important;
                        margin: 0 !important;
                        border: none !important;
                    }
                    .barcode-preview-grid {
                        display: grid !important;
                        grid-template-columns: repeat(${cols}, 1fr) !important;
                        gap: 2.5mm 2.5mm !important;
                        padding: 0 !important;
                        margin: 0 !important;
                        width: 100% !important;
                    }
                    .barcode-label-card {
                        width: 100% !important;
                        height: ${cardH}mm !important;
                        max-height: ${cardH}mm !important;
                        border: 0.5px dashed ${isColor ? accentColor : '#CBD5E1'} !important;
                        border-radius: 4px !important;
                        page-break-inside: avoid !important;
                        break-inside: avoid !important;
                        padding: 0 !important;
                        margin: 0 !important;
                        overflow: hidden !important;
                        background: #FFFFFF !important;
                        display: flex !important;
                        align-items: center !important;
                        justify-content: center !important;
                    }
                    .barcode-label-inner {
                        width: 100% !important;
                        height: 100% !important;
                        display: flex !important;
                        flex-direction: column !important;
                        align-items: center !important;
                        justify-content: space-between !important;
                        box-sizing: border-box !important;
                        padding: ${cardH <= 30 ? '0.8mm 1.2mm' : '1.5mm 2mm'} !important;
                    }
                    .barcode-label-store {
                        font-size: ${cardH <= 30 ? '7.5pt' : '9.5pt'} !important;
                        font-weight: 800 !important;
                        color: ${isColor ? accentColor : '#000000'} !important;
                        line-height: 1.1 !important;
                        margin-bottom: 0.2mm !important;
                        text-align: center !important;
                    }
                    .barcode-label-name {
                        font-size: ${cardH <= 30 ? '8.5pt' : '11pt'} !important;
                        line-height: 1.15 !important;
                        max-height: 2.3em !important;
                        overflow: hidden !important;
                        text-overflow: ellipsis !important;
                        margin-bottom: 0.2mm !important;
                        font-weight: 900 !important;
                        color: #000000 !important;
                        text-align: center !important;
                        width: 100% !important;
                    }
                    .barcode-label-code {
                        font-size: ${cardH <= 30 ? '6.5pt' : '8pt'} !important;
                        font-weight: 700 !important;
                        color: ${isColor ? '#64748B' : '#000000'} !important;
                        line-height: 1 !important;
                        margin-bottom: 0.2mm !important;
                        text-align: center !important;
                    }
                    .barcode-label-inner .price-display-box {
                        padding: 0.3mm 1mm !important;
                        margin: 0.2mm 0 !important;
                        border: none !important;
                        background: transparent !important;
                        display: flex !important;
                        align-items: baseline !important;
                        justify-content: center !important;
                    }
                    .barcode-label-inner .price-display-box .price-number {
                        font-size: ${cardH <= 30 ? '15pt' : '22pt'} !important;
                        line-height: 1 !important;
                        font-weight: 900 !important;
                        color: ${isColor ? accentColor : '#000000'} !important;
                    }
                    .barcode-label-inner .price-display-box .price-prefix {
                        font-size: ${cardH <= 30 ? '8.5pt' : '12pt'} !important;
                        font-weight: 800 !important;
                        color: ${isColor ? accentColor : '#000000'} !important;
                    }
                    .barcode-label-inner .price-display-box span {
                        font-size: ${cardH <= 30 ? '7pt' : '9pt'} !important;
                        font-weight: 700 !important;
                        color: #000000 !important;
                    }
                    .barcode-label-inner .promo-banner {
                        font-size: ${cardH <= 30 ? '6.5pt' : '8.5pt'} !important;
                        font-weight: 800 !important;
                        color: ${isColor ? '#FFFFFF' : '#000000'} !important;
                        background: ${isColor ? accentColor : 'transparent'} !important;
                        border: ${isColor ? 'none' : '0.5px solid #000000'} !important;
                        padding: 0.2mm 0.8mm !important;
                        margin-bottom: 0.2mm !important;
                        line-height: 1 !important;
                        border-radius: 2px !important;
                        text-align: center !important;
                        width: 100% !important;
                    }
                    .barcode-label-inner .date-footer {
                        font-size: ${cardH <= 30 ? '5.5pt' : '7pt'} !important;
                        font-weight: 600 !important;
                        color: #000000 !important;
                        line-height: 1 !important;
                        margin-top: 0.2mm !important;
                        text-align: center !important;
                    }
                `;
            } else {
                // Sticker label sizes (38x28, 40x30, 30x40, 60x40, 80x50, 100x70, or Custom mm)
                docType = 'custom';
                const isSmall = dims.isSmall;
                const isRotated90or270 = rotation === 90 || rotation === 270;
                const safeWidth = Math.max(10, dims.width - 0.6);
                const safeHeight = Math.max(10, dims.height - 0.6);
                const innerW = isRotated90or270 ? safeHeight : safeWidth;
                const innerH = isRotated90or270 ? safeWidth : safeHeight;
                let transformCss = 'none';
                if (rotation === 90) {
                    transformCss = `translateX(${safeWidth}mm) rotate(90deg)`;
                } else if (rotation === 270) {
                    transformCss = `translateY(${safeHeight}mm) rotate(270deg)`;
                } else if (rotation === 180) {
                    transformCss = `translate(${safeWidth}mm, ${safeHeight}mm) rotate(180deg)`;
                }

                customPrintCss = `
                    @page {
                        size: ${dims.width}mm ${dims.height}mm;
                        margin: 0 !important;
                    }
                    * {
                        box-sizing: border-box !important;
                        -webkit-print-color-adjust: exact !important;
                        print-color-adjust: exact !important;
                    }
                    html, body {
                        margin: 0 !important;
                        padding: 0 !important;
                        background: #FFFFFF !important;
                        color: #000000 !important;
                        width: ${dims.width}mm !important;
                        height: auto !important;
                        overflow: hidden !important;
                    }
                    .no-print, .no-print * {
                        display: none !important;
                    }
                    .barcode-preview-container {
                        background: transparent !important;
                        padding: 0 !important;
                        margin: 0 !important;
                        border: none !important;
                    }
                    .barcode-preview-grid {
                        display: block !important;
                        padding: 0 !important;
                        margin: 0 !important;
                        width: ${dims.width}mm !important;
                        font-size: 0 !important;
                        line-height: 0 !important;
                        height: auto !important;
                    }
                    .barcode-label-card {
                        width: ${dims.width}mm !important;
                        height: ${safeHeight}mm !important;
                        max-width: ${dims.width}mm !important;
                        max-height: ${safeHeight}mm !important;
                        min-width: 0 !important;
                        min-height: 0 !important;
                        position: relative !important;
                        box-sizing: border-box !important;
                        padding: 0 !important;
                        margin: 0 auto !important;
                        border: none !important;
                        border-radius: 0 !important;
                        overflow: hidden !important;
                        background: #FFFFFF !important;
                        page-break-inside: avoid !important;
                        break-inside: avoid !important;
                        contain: strict !important;
                    }
                    .barcode-label-card:not(:last-child) {
                        page-break-after: always !important;
                        break-after: page !important;
                    }
                    .barcode-label-card:last-child {
                        page-break-after: avoid !important;
                        break-after: avoid !important;
                    }
                    .barcode-label-inner {
                        position: ${rotation !== 0 ? 'absolute' : 'relative'} !important;
                        top: 0 !important;
                        left: 0 !important;
                        width: ${innerW}mm !important;
                        height: ${innerH}mm !important;
                        max-width: ${innerW}mm !important;
                        max-height: ${innerH}mm !important;
                        transform: ${transformCss} !important;
                        transform-origin: 0 0 !important;
                        display: flex !important;
                        flex-direction: column !important;
                        align-items: center !important;
                        justify-content: center !important;
                        gap: ${isSmall ? '1.8mm' : '2.5mm'} !important;
                        box-sizing: border-box !important;
                        padding: ${isSmall ? '0.6mm 0.8mm' : '2.0mm 3.0mm'} !important;
                        overflow: hidden !important;
                        background: #FFFFFF !important;
                    }
                    .barcode-label-store {
                        font-size: ${isSmall ? '7.5pt' : '11pt'} !important;
                        font-weight: 700 !important;
                        letter-spacing: ${isSmall ? '0.4px' : 'normal'} !important;
                        color: #000000 !important;
                        line-height: 1.1 !important;
                        margin: 0 !important;
                        white-space: nowrap !important;
                        overflow: hidden !important;
                        text-overflow: ellipsis !important;
                        max-width: 100% !important;
                        text-align: center !important;
                        display: block !important;
                    }
                    .barcode-label-name {
                        font-size: ${isSmall ? '8pt' : '12pt'} !important;
                        line-height: 1.12 !important;
                        max-height: ${isSmall ? '2.4em' : '3.2em'} !important;
                        display: -webkit-box !important;
                        -webkit-line-clamp: 2 !important;
                        -webkit-box-orient: vertical !important;
                        overflow: hidden !important;
                        text-overflow: ellipsis !important;
                        word-break: break-word !important;
                        max-width: 100% !important;
                        margin: 0 !important;
                        font-weight: 800 !important;
                        color: #000000 !important;
                        text-align: center !important;
                        width: 100% !important;
                    }
                    .barcode-label-card[data-name-len="very-short"] .barcode-label-name {
                        font-size: ${isSmall ? '11.5pt' : '14pt'} !important;
                    }
                    .barcode-label-card[data-name-len="short"] .barcode-label-name {
                        font-size: ${isSmall ? '9.5pt' : '13pt'} !important;
                    }
                    .barcode-label-card[data-name-len="medium"] .barcode-label-name {
                        font-size: ${isSmall ? '8pt' : '12pt'} !important;
                    }
                    .barcode-label-card[data-name-len="long"] .barcode-label-name {
                        font-size: ${isSmall ? '7.2pt' : '10.5pt'} !important;
                    }
                    .barcode-label-card[data-name-len="very-long"] .barcode-label-name {
                        font-size: ${isSmall ? '6.5pt' : '9.5pt'} !important;
                    }
                    .barcode-label-code {
                        font-size: ${isSmall ? '6.5pt' : '9pt'} !important;
                        font-weight: 600 !important;
                        color: #000000 !important;
                        line-height: 1.0 !important;
                        margin: 0 !important;
                        text-align: center !important;
                        display: block !important;
                    }
                    .barcode-label-inner .price-display-box {
                        padding: 0 !important;
                        margin: 0 !important;
                        border: none !important;
                        background: transparent !important;
                        display: flex !important;
                        align-items: baseline !important;
                        justify-content: center !important;
                        gap: ${isSmall ? '0.8mm' : '1.5mm'} !important;
                        line-height: 1.0 !important;
                        width: 100% !important;
                        white-space: nowrap !important;
                        overflow: hidden !important;
                    }
                    .barcode-label-inner .price-display-box .price-number {
                        font-size: ${isSmall ? '25pt' : '28pt'} !important;
                        line-height: 0.95 !important;
                        font-weight: 900 !important;
                        color: #000000 !important;
                        letter-spacing: -0.5px !important;
                        display: inline-block !important;
                    }
                    .barcode-label-card[data-price-len="1"] .price-number,
                    .barcode-label-card[data-price-len="2"] .price-number {
                        font-size: ${isSmall ? '25pt' : '28pt'} !important;
                    }
                    .barcode-label-card[data-price-len="3"] .price-number {
                        font-size: ${isSmall ? '22pt' : '28pt'} !important;
                    }
                    .barcode-label-card[data-price-len="4"] .price-number,
                    .barcode-label-card[data-price-len="5"] .price-number {
                        font-size: ${isSmall ? '18.5pt' : '26pt'} !important;
                    }
                    .barcode-label-card[data-price-len="6"] .price-number,
                    .barcode-label-card[data-price-len="7"] .price-number {
                        font-size: ${isSmall ? '15pt' : '22pt'} !important;
                    }
                    .barcode-label-card[data-price-len="8"] .price-number,
                    .barcode-label-card[data-price-len="9"] .price-number {
                        font-size: ${isSmall ? '12.5pt' : '18pt'} !important;
                    }
                    .barcode-label-inner .price-display-box .price-prefix {
                        font-size: ${isSmall ? '11pt' : '14pt'} !important;
                        font-weight: 800 !important;
                        color: #000000 !important;
                        display: inline-block !important;
                    }
                    .barcode-label-card[data-price-len="3"] .price-prefix {
                        font-size: ${isSmall ? '10pt' : '13pt'} !important;
                    }
                    .barcode-label-card[data-price-len="4"] .price-prefix,
                    .barcode-label-card[data-price-len="5"] .price-prefix {
                        font-size: ${isSmall ? '9pt' : '12pt'} !important;
                    }
                    .barcode-label-card[data-price-len="6"] .price-prefix,
                    .barcode-label-card[data-price-len="7"] .price-prefix {
                        font-size: ${isSmall ? '8pt' : '11pt'} !important;
                    }
                    .barcode-label-inner .price-display-box span.price-unit {
                        font-size: ${isSmall ? '8.5pt' : '10pt'} !important;
                        font-weight: 700 !important;
                        color: #000000 !important;
                        display: inline-block !important;
                        white-space: nowrap !important;
                    }
                    .barcode-label-card[data-price-len="3"] .price-unit {
                        font-size: ${isSmall ? '8pt' : '9.5pt'} !important;
                    }
                    .barcode-label-card[data-price-len="4"] .price-unit,
                    .barcode-label-card[data-price-len="5"] .price-unit {
                        font-size: ${isSmall ? '7.5pt' : '9pt'} !important;
                    }
                    .barcode-label-card[data-price-len="6"] .price-unit,
                    .barcode-label-card[data-price-len="7"] .price-unit {
                        font-size: ${isSmall ? '7pt' : '8.5pt'} !important;
                    }
                    .barcode-label-inner .promo-banner {
                        font-size: ${isSmall ? '7.5pt' : '10pt'} !important;
                        font-weight: 800 !important;
                        color: #000000 !important;
                        border: 0.5px solid #000000 !important;
                        background: transparent !important;
                        padding: 0.1mm 0.6mm !important;
                        margin: 0 !important;
                        line-height: 1.0 !important;
                        border-radius: 2px !important;
                        text-align: center !important;
                        width: 100% !important;
                        display: block !important;
                        white-space: nowrap !important;
                        overflow: hidden !important;
                        text-overflow: ellipsis !important;
                    }
                    .barcode-label-inner .date-footer {
                        font-size: ${isSmall ? '6pt' : '8pt'} !important;
                        font-weight: 600 !important;
                        color: #000000 !important;
                        line-height: 1.0 !important;
                        margin: 0 !important;
                        text-align: center !important;
                        display: block !important;
                    }
                `;
            }

            printDocument(priceTagGridRef.current, {
                documentType: docType,
                title: 'พิมพ์ราคาสินค้า',
                pageMargin: '0',
                customStyles: customPrintCss
            });
        }
    };

    const getBorderCss = () => {
        if (borderStyle === 'none') return 'none';
        if (borderStyle === 'solid') return '2px solid #E2E8F0';
        if (borderStyle === 'double') return '3px double #CBD5E1';
        return '1.5px dashed #CBD5E1';
    };

    return (
        <div className="barcode-print-page">
            {/* Top Navigation & Action Header */}
            <div className="barcode-header no-print">
                <div className="barcode-header-title">
                    <Button
                        type="default"
                        icon={<FontAwesomeIcon icon="arrow-left" />}
                        onClick={() => navigate('/product')}
                        className="btn-back"
                        data-testid="btn-back-to-products"
                    >
                        กลับ
                    </Button>
                    <div>
                        <h2>🏷️ พิมพ์ราคาสินค้า & ป้ายโปรโมชั่น (Price Tag & Promo Designer)</h2>
                        <span style={{ fontSize: '13px', color: '#64748B' }}>
                            ออกแบบและพิมพ์ป้ายราคา ป้ายติดชั้นวาง ป้ายโปรโมชั่นสำหรับแสดงให้ลูกค้าหน้าร้าน
                        </span>
                    </div>
                </div>
                <div className="barcode-header-actions">
                    <Button
                        type="default"
                        danger
                        icon={<FontAwesomeIcon icon="trash-can" />}
                        onClick={handleClearAll}
                        disabled={selectedItems.length === 0}
                        data-testid="btn-clear-all"
                    >
                        ล้างคิวพิมพ์ ({selectedItems.length})
                    </Button>
                    <Button
                        type="primary"
                        icon={<FontAwesomeIcon icon="print" />}
                        onClick={handlePrint}
                        disabled={previewLabels.length === 0}
                        style={{
                            background: 'linear-gradient(135deg, #FFA07A 0%, #FF6B4A 100%)',
                            border: 'none',
                            fontWeight: 700,
                            borderRadius: '10px',
                            boxShadow: '0 3px 10px rgba(255, 122, 89, 0.3)'
                        }}
                        data-testid="btn-trigger-print"
                    >
                        🖨️ สั่งพิมพ์ป้ายราคา ({totalLabelsCount} ป้าย)
                    </Button>
                </div>
            </div>

            <div className="barcode-layout">
                {/* Left Side: Product Selector & Designer Settings */}
                <div className="barcode-control-panel no-print">
                    {/* 1. Select Products */}
                    <div className="barcode-card">
                        <div className="barcode-card-title">
                            <span>🔍 1. เลือกสินค้าสำหรับพิมพ์ป้าย</span>
                            {filteredProductOptions.length > 0 && (
                                <Button
                                    type="link"
                                    size="small"
                                    onClick={handleAddAllFiltered}
                                    style={{ padding: 0 }}
                                    data-testid="btn-add-all-filtered"
                                >
                                    + เพิ่มทั้งหมด ({filteredProductOptions.length})
                                </Button>
                            )}
                        </div>

                        <div className="barcode-search-box">
                            <Input
                                placeholder="ค้นหาตามชื่อสินค้า หรือ รหัสสินค้า..."
                                prefix={<FontAwesomeIcon icon="magnifying-glass" style={{ color: '#94A3B8' }} />}
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                allowClear
                                data-testid="input-search-product"
                            />
                        </div>

                        <Select
                            showSearch
                            placeholder="คลิกเพื่อค้นหาและเลือกสินค้า..."
                            style={{ width: '100%', marginBottom: '14px' }}
                            optionFilterProp="children"
                            onChange={handleAddProduct}
                            value={undefined}
                            data-testid="select-product-dropdown"
                        >
                            {filteredProductOptions.map(p => (
                                <Select.Option key={p.id} value={p.id}>
                                    [{p.product_code}] {p.product_name} - ฿{Number(p.price_retail || 0).toLocaleString()}
                                </Select.Option>
                            ))}
                        </Select>

                        <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                            {selectedItems.length === 0 ? (
                                <Empty description="ยังไม่ได้เลือกสินค้าเข้าสู่คิวพิมพ์" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                            ) : (
                                selectedItems.map(item => (
                                    <div key={item.id} className="barcode-item-row" data-testid={`selected-item-${item.id}`}>
                                        <div className="barcode-item-info">
                                            <div className="barcode-item-name" title={item.product_name}>
                                                {item.product_name}
                                            </div>
                                            <div className="barcode-item-code">
                                                <Tag color={item.product_code.startsWith('SKU') ? 'orange' : 'blue'}>
                                                    {item.product_code}
                                                </Tag>
                                                ฿{Number(item.price_retail).toLocaleString()}
                                            </div>
                                        </div>
                                        <div className="barcode-item-qty">
                                            <InputNumber
                                                min={1}
                                                max={500}
                                                value={item.copies}
                                                onChange={(val) => handleUpdateCopies(item.id, Number(val || 1))}
                                                style={{ width: '65px' }}
                                                data-testid={`input-copies-${item.id}`}
                                            />
                                            <Button
                                                type="text"
                                                danger
                                                icon={<FontAwesomeIcon icon="xmark" />}
                                                onClick={() => handleRemoveItem(item.id)}
                                                data-testid={`btn-remove-${item.id}`}
                                            />
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>

                    {/* 2. Tag Template & Layout Settings */}
                    <div className="barcode-card">
                        <div className="barcode-card-title">
                            <span>🎨 2. รูปแบบป้ายราคา (Tag Template)</span>
                        </div>

                        <div style={{ marginBottom: '14px' }}>
                            <Radio.Group 
                                value={templateStyle} 
                                onChange={(e) => setTemplateStyle(e.target.value)}
                                buttonStyle="solid"
                                style={{ width: '100%', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}
                                className="price-tag-template-group"
                            >
                                <Radio.Button value="shelf" style={{ textAlign: 'center', borderRadius: '8px' }}>
                                    🏷️ ติดชั้นวาง (Shelf)
                                </Radio.Button>
                                <Radio.Button value="promo" style={{ textAlign: 'center', borderRadius: '8px' }}>
                                    🔥 โปรโมชั่น (SALE)
                                </Radio.Button>
                                <Radio.Button value="standard" style={{ textAlign: 'center', borderRadius: '8px' }}>
                                    🏪 มาตรฐานร้านค้า
                                </Radio.Button>
                                <Radio.Button value="minimal" style={{ textAlign: 'center', borderRadius: '8px' }}>
                                    ✨ เรียบง่าย (Minimal)
                                </Radio.Button>
                            </Radio.Group>
                        </div>

                        {templateStyle === 'promo' && (
                            <div style={{ marginBottom: '14px', background: '#FEF2F2', padding: '10px', borderRadius: '8px', border: '1px solid #FECACA' }}>
                                <label style={{ fontSize: '12px', fontWeight: 600, color: '#991B1B', display: 'block', marginBottom: '4px' }}>
                                    ข้อความแถบโปรโมชั่นด้านบน
                                </label>
                                <Input
                                    value={promoText}
                                    onChange={(e) => setPromoText(e.target.value)}
                                    placeholder="เช่น SALE! 50% / ซื้อ 1 แถม 1"
                                />
                            </div>
                        )}

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            <div>
                                <label style={{ fontSize: '13px', fontWeight: 600, display: 'block', marginBottom: '4px' }}>
                                    ชื่อร้านค้าบนป้าย
                                </label>
                                <Input
                                    value={storeName}
                                    onChange={(e) => setStoreName(e.target.value)}
                                    placeholder="ชื่อร้าน..."
                                    disabled={!showStoreName}
                                    data-testid="input-store-name"
                                />
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ fontSize: '13px' }}>แสดงชื่อร้านค้า</span>
                                <Switch checked={showStoreName} onChange={setShowStoreName} />
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ fontSize: '13px' }}>แสดงชื่อสินค้า</span>
                                <Switch checked={showProductName} onChange={setShowProductName} />
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ fontSize: '13px' }}>แสดงรหัสสินค้า (Product Code)</span>
                                <Switch checked={showProductCode} onChange={setShowProductCode} />
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ fontSize: '13px' }}>แสดงราคาสินค้า</span>
                                <Switch checked={showPrice} onChange={setShowPrice} />
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ fontSize: '13px' }}>แสดงหน่วยนับ (/ชิ้น, /แพ็ค)</span>
                                <Switch checked={showUnit} onChange={setShowUnit} />
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ fontSize: '13px' }}>แสดงวันที่พิมพ์</span>
                                <Switch checked={showDate} onChange={setShowDate} />
                            </div>

                            {/* Color Accent Picker */}
                            <div>
                                <label style={{ fontSize: '13px', fontWeight: 600, display: 'block', marginBottom: '4px' }}>
                                    โทนสีเน้นป้ายราคา (Theme Color)
                                </label>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    {[
                                        { color: '#FF7A59', name: 'ส้มพีช Jinda' },
                                        { color: '#DC2626', name: 'แดงโปรโมชั่น' },
                                        { color: '#2563EB', name: 'น้ำเงินทางการ' },
                                        { color: '#16A34A', name: 'เขียวสดชื่น' },
                                        { color: '#1E293B', name: 'ดำคลาสสิก' }
                                    ].map(c => (
                                        <button
                                            key={c.color}
                                            type="button"
                                            onClick={() => setAccentColor(c.color)}
                                            style={{
                                                flex: 1,
                                                height: '32px',
                                                background: c.color,
                                                border: accentColor === c.color ? '3px solid #FFFFFF' : '1px solid rgba(0,0,0,0.1)',
                                                outline: accentColor === c.color ? '2px solid #FF7A59' : 'none',
                                                borderRadius: '8px',
                                                cursor: 'pointer'
                                            }}
                                            title={c.name}
                                        />
                                    ))}
                                </div>
                            </div>

                            {/* Tag Size Selection */}
                            <div>
                                <label style={{ fontSize: '13px', fontWeight: 600, display: 'block', marginBottom: '4px' }}>
                                    ขนาดป้าย / กระดาษพิมพ์
                                </label>
                                <Select
                                    value={tagSize}
                                    onChange={setTagSize}
                                    style={{ width: '100%' }}
                                    options={[
                                        { value: '38x28', label: '38 × 28 mm (ขนาดสติ๊กเกอร์มาตรฐาน / ขนาดพิมพ์พอดีดวง)' },
                                        { value: '40x30', label: '40 × 30 mm (Xprinter XP-420B / สติ๊กเกอร์ขนาดเล็ก)' },
                                        { value: '30x40', label: '30 × 40 mm (สลับด้าน - สำหรับ Driver บางรุ่น)' },
                                        { value: 'shelf', label: '80 × 50 mm (ป้ายติดชั้นวาง / เชลฟ์ - ยอดนิยม)' },
                                        { value: 'standard', label: '60 × 40 mm (มาตรฐาน - ป้ายราคาสินค้าทั่วไป)' },
                                        { value: 'large', label: '100 × 70 mm (ป้ายใหญ่พิเศษ - หน้าร้าน/ป๊อปอัพ)' },
                                        { value: 'compact', label: '35 × 25 mm (ดวงเล็ก - สติกเกอร์ขนาดเล็ก)' },
                                        { value: 'thermal58', label: 'ม้วนกระดาษความร้อน 58mm (Thermal Slip 58mm)' },
                                        { value: 'thermal80', label: 'ม้วนกระดาษความร้อน 80mm (Thermal Slip 80mm)' },
                                        { value: 'a4', label: 'กระดาษ A4 (สติ๊กเกอร์ A4 จัดเรียง 24-40 ป้าย/แผ่น)' },
                                        { value: 'custom', label: '⚙️ กำหนดขนาดเอง (Custom Size)' }
                                    ]}
                                    data-testid="select-tag-size"
                                />

                                {tagSize === 'custom' && (
                                    <div style={{
                                        display: 'grid',
                                        gridTemplateColumns: '1fr 1fr',
                                        gap: '10px',
                                        marginTop: '10px',
                                        padding: '12px',
                                        background: 'var(--bg-subtle, #F8FAFC)',
                                        borderRadius: '8px',
                                        border: '1px solid var(--border-color, #E2E8F0)'
                                    }} data-testid="custom-size-inputs">
                                        <div>
                                            <label style={{ fontSize: '12px', fontWeight: 600, display: 'block', marginBottom: '4px' }}>
                                                ความกว้าง (Width)
                                            </label>
                                            <InputNumber
                                                min={10}
                                                max={300}
                                                step={1}
                                                precision={1}
                                                value={customWidth}
                                                onChange={handleCustomWidthChange}
                                                addonAfter="mm"
                                                style={{ width: '100%' }}
                                                data-testid="input-custom-width"
                                            />
                                        </div>
                                        <div>
                                            <label style={{ fontSize: '12px', fontWeight: 600, display: 'block', marginBottom: '4px' }}>
                                                ความสูง (Height)
                                            </label>
                                            <InputNumber
                                                min={10}
                                                max={300}
                                                step={1}
                                                precision={1}
                                                value={customHeight}
                                                onChange={handleCustomHeightChange}
                                                addonAfter="mm"
                                                style={{ width: '100%' }}
                                                data-testid="input-custom-height"
                                            />
                                        </div>
                                    </div>
                                )}
                                {tagSize === 'a4' && (
                                    <div style={{
                                        marginTop: '10px',
                                        padding: '12px',
                                        background: 'var(--bg-subtle, #F8FAFC)',
                                        borderRadius: '8px',
                                        border: '1px solid var(--border-color, #E2E8F0)'
                                    }} data-testid="a4-template-size-box">
                                        <label style={{ fontSize: '12px', fontWeight: 600, display: 'block', marginBottom: '4px' }}>
                                            📐 ขนาดแม่แบบสติ๊กเกอร์บนแผ่น A4
                                        </label>
                                        <Select
                                            value={a4GridSize}
                                            onChange={setA4GridSize}
                                            style={{ width: '100%' }}
                                            options={[
                                                { value: '38x28', label: '38 × 28 mm (4 คอลัมน์ ~ 36-40 ดวง/แผ่น - ดวงเล็ก)' },
                                                { value: '50x30', label: '50 × 30 mm (3 คอลัมน์ ~ 21-24 ดวง/แผ่น - ดวงกลาง)' },
                                                { value: '65x35', label: '65 × 35 mm (3 คอลัมน์ ~ 18-21 ดวง/แผ่น - สติ๊กเกอร์มาตรฐาน A4)' },
                                                { value: '80x50', label: '80 × 50 mm (2 คอลัมน์ ~ 8-10 ดวง/แผ่น - ป้ายเชลฟ์ใหญ่)' }
                                            ]}
                                            data-testid="select-a4-grid-size"
                                        />
                                    </div>
                                )}
                            </div>

                            {/* Print Color Mode (Color vs Black & White) */}
                            <div>
                                <label style={{ fontSize: '13px', fontWeight: 600, display: 'block', marginBottom: '4px' }}>
                                    🎨 รูปแบบสีการพิมพ์ (Print Color Mode)
                                </label>
                                <Radio.Group 
                                    value={printColorMode} 
                                    onChange={(e) => setPrintColorMode(e.target.value)}
                                    buttonStyle="solid"
                                    style={{ width: '100%', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}
                                    data-testid="radio-print-color-mode"
                                >
                                    <Radio.Button value="mono" style={{ textAlign: 'center', borderRadius: '8px', fontSize: '12.5px' }}>
                                        ⚫ ขาวดำ (สติ๊กเกอร์)
                                    </Radio.Button>
                                    <Radio.Button value="color" style={{ textAlign: 'center', borderRadius: '8px', fontSize: '12.5px' }}>
                                        🎨 พิมพ์สี (สำหรับ A4)
                                    </Radio.Button>
                                </Radio.Group>
                            </div>

                            {/* Rotation & Orientation Control */}
                            <div>
                                <label style={{ fontSize: '13px', fontWeight: 600, display: 'block', marginBottom: '4px' }}>
                                    🔄 การหมุนป้าย / ทิศทางการพิมพ์ (Orientation & Rotation)
                                </label>
                                <Select
                                    value={rotation}
                                    onChange={handleRotationChange}
                                    style={{ width: '100%' }}
                                    options={[
                                        { value: 0, label: '0° (ปกติ - ไม่หมุน)' },
                                        { value: 90, label: '90° (หมุน 90° ตามเข็ม - แก้ปัญหาป้ายออกแนวนอน)' },
                                        { value: 270, label: '270° (หมุน 90° ทวนเข็ม)' },
                                        { value: 180, label: '180° (กลับหัว 180°)' }
                                    ]}
                                    data-testid="select-print-rotation"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right Side: Live Price Tag Preview */}
                <div className="barcode-preview-container">
                    <div className="barcode-card-title no-print" style={{ borderBottom: '1px solid #E2E8F0', paddingBottom: '12px' }}>
                        <div>
                            <span style={{ fontSize: '16px' }}>👁️ ตัวอย่างป้ายราคาสำหรับลูกค้าหน้าร้าน (Live Preview)</span>
                            <div style={{ fontSize: '12px', color: '#64748B', fontWeight: 400, marginTop: '2px' }}>
                                ทั้งหมด {previewLabels.length} ป้าย | ขนาด: {(() => {
                                    const d = getEffectiveDimensions();
                                    return d.isContinuous ? `${d.width}mm ม้วนความร้อน` : d.isA4 ? 'A4' : `${d.width} × ${d.height} mm`;
                                })()} | การหมุน: {rotation}° | รูปแบบ: {templateStyle === 'shelf' ? 'ติดชั้นวาง (เน้นราคา)' : templateStyle === 'promo' ? 'โปรโมชั่น (SALE)' : templateStyle === 'standard' ? 'มาตรฐานร้านค้า' : 'มินิมอล'}
                            </div>
                        </div>
                        <Tag color="green" style={{ fontWeight: 600, padding: '4px 10px', borderRadius: '8px' }}>
                            ✨ ป้ายราคาและโปรโมชั่นหน้าร้าน (Customer Price Tag)
                        </Tag>
                    </div>

                    {previewLabels.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '80px 0' }} className="no-print">
                            <Empty description="กรุณาเลือกสินค้าทางซ้ายมือเพื่อดูตัวอย่างป้ายราคาและสั่งพิมพ์" />
                        </div>
                    ) : (
                        <div ref={priceTagGridRef} className={`barcode-preview-grid grid-size-${tagSize}`} data-testid="price-tag-preview-grid">
                            {previewLabels.map((item, index) => {
                                const dims = getEffectiveDimensions();
                                const priceLen = String(Number(item.price_retail || 0).toLocaleString()).length;
                                const priceConfig = getAdaptivePriceFontSize(item.price_retail, dims.isSmall, true);
                                const nameCategory = getAdaptiveProductNameCategory(item.product_name);
                                const nameFontSize = getAdaptiveProductNameFontSize(item.product_name, dims.isSmall, true);
                                const isRotatedView = (rotation === 90 || rotation === 270) && !dims.isContinuous && !dims.isA4;
                                return (
                                    <div 
                                        key={`${item.id}-${index}`} 
                                        className={`barcode-label-card card-size-${tagSize} template-${templateStyle}`}
                                        data-price-len={priceLen}
                                        data-name-len={nameCategory}
                                        style={{
                                            border: getBorderCss(),
                                            borderColor: borderStyle !== 'none' ? '#CBD5E1' : 'transparent',
                                            borderRadius: '8px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            aspectRatio: (!dims.isContinuous && !dims.isA4) ? (isRotatedView ? `${dims.height} / ${dims.width}` : `${dims.width} / ${dims.height}`) : undefined,
                                            width: dims.isSmall ? (isRotatedView ? '145px' : '180px') : dims.width <= 65 ? '220px' : '260px',
                                            minHeight: dims.isContinuous ? (dims.width === 58 ? '100px' : '130px') : undefined,
                                            boxSizing: 'border-box',
                                            position: 'relative',
                                            overflow: 'hidden',
                                            background: '#FFFFFF'
                                        }}
                                    >
                                        <div 
                                            className="barcode-label-inner"
                                            style={{
                                                width: '100%',
                                                height: '100%',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: dims.isSmall ? '6px' : '10px',
                                                boxSizing: 'border-box',
                                                padding: dims.isSmall ? '6px 8px' : '10px 14px'
                                            }}
                                        >
                                            {/* Promotional Banner Header */}
                                            {templateStyle === 'promo' && (
                                                <div className="promo-banner" style={{
                                                    background: accentColor,
                                                    color: '#FFFFFF',
                                                    fontSize: dims.isSmall ? '9.5px' : '11px',
                                                    fontWeight: 800,
                                                    padding: dims.isSmall ? '1px 4px' : '3px 6px',
                                                    borderRadius: '4px',
                                                    margin: 0,
                                                    letterSpacing: '0.3px',
                                                    width: '100%',
                                                    textAlign: 'center',
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis'
                                                }}>
                                                    {promoText}
                                                </div>
                                            )}

                                            {/* Store Name Header */}
                                            {showStoreName && Boolean(storeName) && (
                                                <div className="barcode-label-store" style={{ color: printColorMode === 'color' ? accentColor : '#000000', fontSize: dims.isSmall ? '10px' : '12px', fontWeight: 700, letterSpacing: dims.isSmall ? '0.4px' : 'normal', margin: 0, textAlign: 'center', width: '100%', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                    {storeName}
                                                </div>
                                            )}

                                            {/* Product Name */}
                                            {showProductName && (
                                                <div 
                                                    className="barcode-label-name" 
                                                    title={item.product_name}
                                                    style={{
                                                        fontSize: nameFontSize,
                                                        fontWeight: 800,
                                                        color: '#1E293B',
                                                        margin: 0,
                                                        lineHeight: 1.15,
                                                        textAlign: 'center',
                                                        width: '100%',
                                                        display: '-webkit-box',
                                                        WebkitLineClamp: 2,
                                                        WebkitBoxOrient: 'vertical',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        wordBreak: 'break-word'
                                                    }}
                                                >
                                                    {item.product_name}
                                                </div>
                                            )}

                                            {/* Product Code Text */}
                                            {showProductCode && (
                                                <div className="barcode-label-code" style={{ fontSize: dims.isSmall ? '8px' : '11px', color: '#64748B', fontFamily: 'monospace', margin: 0, textAlign: 'center', width: '100%', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                    รหัส: {item.product_code}
                                                </div>
                                            )}

                                            {/* Big Bold Customer Price Display */}
                                            {showPrice && (
                                                <div className="price-display-box" style={{
                                                    display: 'flex',
                                                    alignItems: 'baseline',
                                                    justifyContent: 'center',
                                                    gap: dims.isSmall ? '3px' : '4px',
                                                    margin: 0,
                                                    padding: dims.isSmall ? '2px 4px' : '4px 8px',
                                                    width: '100%',
                                                    boxSizing: 'border-box',
                                                    background: templateStyle === 'promo' ? '#FEF2F2' : templateStyle === 'shelf' ? '#FFF7ED' : '#F8FAFC',
                                                    borderRadius: '6px',
                                                    border: `1px solid ${templateStyle === 'promo' ? '#FECACA' : templateStyle === 'shelf' ? '#FFEDD5' : '#E2E8F0'}`,
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden'
                                                }}>
                                                    <span className="price-prefix" style={{
                                                        fontSize: priceConfig.prefixSize,
                                                        fontWeight: 700,
                                                        color: templateStyle === 'promo' ? accentColor : printColorMode === 'color' ? accentColor : '#000000',
                                                        lineHeight: 1
                                                    }}>
                                                        {pricePrefix}
                                                    </span>
                                                    <span className="price-number" style={{
                                                        fontSize: priceConfig.priceSize,
                                                        fontWeight: 900,
                                                        color: templateStyle === 'promo' ? accentColor : printColorMode === 'color' ? accentColor : '#000000',
                                                        lineHeight: 0.9,
                                                        letterSpacing: '-0.5px'
                                                    }}>
                                                        {Number(item.price_retail || 0).toLocaleString()}
                                                    </span>
                                                    {showUnit && (
                                                        <span className="price-unit" style={{
                                                            fontSize: priceConfig.unitSize,
                                                            color: '#64748B',
                                                            fontWeight: 600,
                                                            lineHeight: 1,
                                                            whiteSpace: 'nowrap'
                                                        }}>
                                                            /{item.product_unit_name || 'ชิ้น'}
                                                        </span>
                                                    )}
                                                </div>
                                            )}

                                            {/* Date Footer */}
                                            {showDate && (
                                                <div className="date-footer" style={{ fontSize: dims.isSmall ? '7.5px' : '9.5px', color: '#94A3B8', margin: 0, textAlign: 'center', width: '100%' }}>
                                                    พิมพ์เมื่อ: {new Date().toLocaleDateString('th-TH')}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PriceTagPrint;
